import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { ALL_PERMISSIONS, type TeamPermission } from "@/courier/lib/teamPermissions";

/**
 * What the signed-in login is allowed to do inside a courier business.
 *
 * The owner (the login the courier account was created with) always has every
 * ability and can never be locked out. A team member has exactly the abilities
 * stored on their membership row.
 */
export function useCourierPermissions() {
  const { user } = useAuth();
  const [isOwner, setIsOwner] = useState(false);
  const [courierId, setCourierId] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setIsOwner(false); setCourierId(null); setPermissions(new Set()); setRole(null); setReady(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const [ownedRes, memberRes] = await Promise.all([
        supabase.from("couriers").select("id").eq("profile_id", user.id).limit(1),
        supabase
          .from("courier_team_members" as any)
          .select("courier_id, role, permissions")
          .eq("user_id", user.id)
          .eq("is_active", true)
          .limit(1),
      ]);
      if (cancelled) return;
      const owned = (ownedRes.data ?? []) as { id: string }[];
      if (owned.length) {
        setIsOwner(true);
        setCourierId(owned[0].id);
        setRole("owner");
        setPermissions(new Set(ALL_PERMISSIONS));
        setReady(true);
        return;
      }
      const member = ((memberRes.data ?? []) as any[])[0];
      if (member) {
        setIsOwner(false);
        setCourierId(member.courier_id);
        setRole(member.role);
        setPermissions(new Set<string>(member.permissions ?? []));
      } else {
        // A courier login with no `couriers` row yet is still their own owner.
        setIsOwner(true);
        setCourierId(user.id);
        setRole("owner");
        setPermissions(new Set(ALL_PERMISSIONS));
      }
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const can = (perm: TeamPermission) => isOwner || permissions.has(perm);

  return { isOwner, courierId, role, permissions, can, ready, isTeamMember: !isOwner && !!role };
}

export default useCourierPermissions;
